import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Archive,
  CalendarClock,
  CheckCircle2,
  Clock3,
  FileText,
  History,
  Loader2,
  Paperclip,
  Plus,
  Save,
  ShieldCheck,
  X,
} from "lucide-react";

import TitleButtons from "../../../../../components/TitleButtons";
import { useTheme } from "../../../../../context/ThemeContext";
import { useAuth } from "../../../../../context/auth-context";

type SectionKey =
  | "visao-geral"
  | "a-vencer"
  | "vencidas"
  | "anexados"
  | "historico";

type StatusValidade = "regular" | "a_vencer" | "vencido" | "sem_validade";

type Periodicidade =
  | "semanal"
  | "mensal"
  | "trimestral"
  | "semestral"
  | "anual"
  | "sem_validade"
  | "sob_demanda";

type TipoValidade =
  | "com_vencimento"
  | "sem_vencimento"
  | "sob_demanda"
  | "revisao_periodica";

type OperacionalKey =
  | "ok"
  | "pendente_anexo"
  | "vencida"
  | "renovacao_pendente"
  | "aguardando_validacao"
  | "pendente"
  | "nao_exigido";

type AnexoFilter = "vinculado" | "pendente";

type PortalResumo = {
  totalExigem: number;
  anexadosAtualizados: number;
  pendenteAnexo: number;
  vencidos: number;
  renovacaoPendente: number;
  aguardandoValidacao: number;
};

type OperacionalVisual = {
  key: OperacionalKey;
  label: string;
  background: string;
  color: string;
  border: string;
};

type CertidaoItem = {
  id: number;
  nome: string;
  categoria: string;
  orgaoEmissor: string;
  empresa: string;
  cnpj: string;

  dataEmissao: string | null;
  dataValidade: string | null;
  dataUpload?: string | null;

  tipoValidade: TipoValidade;
  periodicidade: Periodicidade;

  areaResponsavel: string;
  responsavelNome?: string | null;

  statusValidade: StatusValidade;

  anexoDisponivel: boolean;
  arquivoVigenteId?: number | null;
  arquivoNome?: string | null;
  s3Key?: string | null;

  observacao?: string | null;

  versao: number;
  vigente: boolean;
  obsoleto: boolean;
  documentoSubstituidoId?: number | null;
  dataSubstituicao?: string | null;

  portais: PortalResumo;
};

type ApiDocumentoItem = {
  id: number;
  empresaId?: number | string | null;
  moduloId?: number | string | null;

  nome?: string | null;
  categoria?: string | null;
  orgaoEmissor?: string | null;

  tipoValidade?: TipoValidade | string | null;
  periodicidade?: Periodicidade | string | null;

  areaResponsavel?: string | null;
  responsavelNome?: string | null;

  arquivoVigenteId?: number | null;
  arquivoNome?: string | null;
  s3Bucket?: string | null;
  s3Key?: string | null;

  dataEmissao?: string | null;
  dataValidade?: string | null;
  dataUpload?: string | null;

  statusValidade?: StatusValidade | string | null;
  anexoDisponivel?: boolean | null;

  portais?: {
    totalExigem?: number | string | null;
    anexadosAtualizados?: number | string | null;
    pendenteAnexo?: number | string | null;
    renovacaoPendente?: number | string | null;
    emRenovacao?: number | string | null;
    vencidos?: number | string | null;
    aguardandoValidacao?: number | string | null;
  } | null;
};

type ApiDocumentosResponse = {
  ok?: boolean;
  items?: ApiDocumentoItem[];
  erro?: string;
};

type ApiPostDocumentoResponse = {
  ok?: boolean;
  documento?: {
    id?: number | string;
    [key: string]: any;
  };
  erro?: string;
};

type ApiPresignUploadResponse = {
  ok?: boolean;
  bucket?: string;
  key?: string;
  uploadUrl?: string;
  expiresIn?: number;
  mimeType?: string;
  originalName?: string;
  sizeBytes?: number;
  erro?: string;
};

type ApiRegistrarAnexoResponse = {
  ok?: boolean;
  arquivo?: any;
  erro?: string;
};

type ColumnFilters = {
  documento: string;
  categoria: string;
  orgaoEmissor: string;
  validade: string;
  regra: string;
  responsavel: string;
  statusValidade: string;
  operacional: string;
  anexo: string;
};

type RegisterDocumentForm = {
  nome: string;
  categoria: string;
  orgaoEmissor: string;
  descricao: string;
  tipoValidade: TipoValidade;
  periodicidade: Periodicidade;
  areaResponsavel: string;
  responsavelNome: string;
  dataEmissao: string;
  dataValidade: string;
  uploadObservacao: string;
  file: File | null;
};

type CertidoesDocumentosLayoutProps = {
  section?: SectionKey;
};

const API_BASE = "https://api.logvi-havox.com";
const API_DOCUMENTOS_URL = `${API_BASE}/template/governanca/documentos`;
const API_PRESIGN_UPLOAD_URL = `${API_BASE}/template/governanca/documentos/anexos/presign-upload`;
const API_REGISTRAR_ANEXO_URL = `${API_BASE}/template/governanca/documentos/anexos/registrar`;

const BASE_PATH = "/governanca-qualidade-e-compliance/certidoes-e-documentos";

const DEPARTMENT_COLOR = "#2f3f59";

const sections: Array<{
  key: SectionKey;
  label: string;
  path: string;
  icon: React.ReactNode;
}> = [
  {
    key: "visao-geral",
    label: "Visão geral",
    path: `${BASE_PATH}/visao-geral`,
    icon: <ShieldCheck size={14} />,
  },
  {
    key: "a-vencer",
    label: "A vencer",
    path: `${BASE_PATH}/a-vencer`,
    icon: <CalendarClock size={14} />,
  },
  {
    key: "vencidas",
    label: "Vencidas",
    path: `${BASE_PATH}/vencidas`,
    icon: <AlertTriangle size={14} />,
  },
  {
    key: "anexados",
    label: "Anexados",
    path: `${BASE_PATH}/anexados`,
    icon: <Paperclip size={14} />,
  },
  {
    key: "historico",
    label: "Histórico",
    path: `${BASE_PATH}/historico`,
    icon: <History size={14} />,
  },
];

const initialFilters: ColumnFilters = {
  documento: "",
  categoria: "",
  orgaoEmissor: "",
  validade: "",
  regra: "",
  responsavel: "",
  statusValidade: "",
  operacional: "",
  anexo: "",
};

const initialRegisterForm: RegisterDocumentForm = {
  nome: "",
  categoria: "",
  orgaoEmissor: "",
  descricao: "",
  tipoValidade: "com_vencimento",
  periodicidade: "sob_demanda",
  areaResponsavel: "",
  responsavelNome: "",
  dataEmissao: "",
  dataValidade: "",
  uploadObservacao: "",
  file: null,
};

function normalizeText(value: unknown) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

function hexToRgbComponents(hex: string | undefined): [number, number, number] {
  if (!hex) return [47, 63, 89];

  const clean = hex.replace("#", "").trim();

  if (clean.length !== 6) return [47, 63, 89];

  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);

  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
    return [47, 63, 89];
  }

  return [r, g, b];
}

function toRgba(hex: string | undefined, alpha = 1) {
  const [r, g, b] = hexToRgbComponents(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function toNumber(value: unknown, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function toNullableString(value: unknown): string | null {
  if (value === undefined || value === null) return null;
  const str = String(value).trim();
  return str ? str : null;
}

function normalizeApiDate(value: string | null | undefined): string | null {
  if (!value) return null;

  const raw = String(value).trim();

  if (!raw) return null;

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
    return raw;
  }

  const isoDate = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (isoDate) {
    const [, year, month, day] = isoDate;
    return `${day}/${month}/${year}`;
  }

  return raw;
}

function parseDateFlexible(value: string | null): Date | null {
  if (!value) return null;

  const raw = String(value).trim();

  const br = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (br) {
    const [, dd, mm, yyyy] = br;
    const date = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const [, yyyy, mm, dd] = iso;
    const date = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  return null;
}

function startOfDay(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function getDaysDiffFromToday(date: Date | null) {
  if (!date) return null;

  const today = startOfDay(new Date());
  const target = startOfDay(date);
  const diffMs = target.getTime() - today.getTime();

  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

function formatMesesDias(totalDias: number) {
  const diasAbsolutos = Math.abs(totalDias);

  if (diasAbsolutos < 30) return null;

  const meses = Math.floor(diasAbsolutos / 30);
  const dias = diasAbsolutos % 30;

  const mesesTexto = meses === 1 ? "1 mês" : `${meses} meses`;
  const diasTexto = dias === 1 ? "1 dia" : `${dias} dias`;

  if (dias === 0) return mesesTexto;

  return `${mesesTexto} e ${diasTexto}`;
}

function getPrazoOperacional(item: CertidaoItem) {
  if (item.tipoValidade === "sem_vencimento") {
    return "Sem vencimento";
  }

  if (item.tipoValidade === "sob_demanda") {
    return "Sob demanda";
  }

  if (item.tipoValidade === "revisao_periodica" && !item.dataValidade) {
    return "Revisão periódica";
  }

  const validade = parseDateFlexible(item.dataValidade);
  const dias = getDaysDiffFromToday(validade);

  if (dias === null) {
    return "Validade não informada";
  }

  const mesesDias = formatMesesDias(dias);
  const complemento = mesesDias ? ` (${mesesDias})` : "";

  if (dias > 1) return `Faltam ${dias} dias${complemento}`;
  if (dias === 1) return "Falta 1 dia";
  if (dias === 0) return "Vence hoje";
  if (dias === -1) return "Vencida há 1 dia";

  return `Vencida há ${Math.abs(dias)} dias${complemento}`;
}

function pluralPortal(qtd: number) {
  return qtd === 1 ? "portal" : "portais";
}

function normalizeTipoValidade(value: unknown): TipoValidade {
  const raw = String(value || "").trim();

  if (
    raw === "com_vencimento" ||
    raw === "sem_vencimento" ||
    raw === "sob_demanda" ||
    raw === "revisao_periodica"
  ) {
    return raw;
  }

  return "com_vencimento";
}

function normalizePeriodicidade(value: unknown): Periodicidade {
  const raw = String(value || "").trim();

  if (
    raw === "semanal" ||
    raw === "mensal" ||
    raw === "trimestral" ||
    raw === "semestral" ||
    raw === "anual" ||
    raw === "sem_validade" ||
    raw === "sob_demanda"
  ) {
    return raw;
  }

  return "sob_demanda";
}

function normalizeStatusValidade(value: unknown): StatusValidade {
  const raw = String(value || "").trim();

  if (
    raw === "regular" ||
    raw === "a_vencer" ||
    raw === "vencido" ||
    raw === "sem_validade"
  ) {
    return raw;
  }

  return "sem_validade";
}

function mapApiItemToCertidaoItem(
  item: ApiDocumentoItem,
  sessaoEmpresaNome?: string | null,
  sessaoEmpresaCnpj?: string | null
): CertidaoItem {
  const portais = item.portais || {};

  return {
    id: Number(item.id),
    nome: toNullableString(item.nome) || "Documento sem nome",
    categoria: toNullableString(item.categoria) || "Sem categoria",
    orgaoEmissor: toNullableString(item.orgaoEmissor) || "Não informado",

    empresa: sessaoEmpresaNome || "Empresa não informada",
    cnpj: sessaoEmpresaCnpj || "",

    dataEmissao: normalizeApiDate(item.dataEmissao),
    dataValidade: normalizeApiDate(item.dataValidade),
    dataUpload: normalizeApiDate(item.dataUpload),

    tipoValidade: normalizeTipoValidade(item.tipoValidade),
    periodicidade: normalizePeriodicidade(item.periodicidade),

    areaResponsavel: toNullableString(item.areaResponsavel) || "Não informado",
    responsavelNome: toNullableString(item.responsavelNome),

    statusValidade: normalizeStatusValidade(item.statusValidade),

    anexoDisponivel: Boolean(item.anexoDisponivel || item.arquivoVigenteId),
    arquivoVigenteId:
      item.arquivoVigenteId === undefined || item.arquivoVigenteId === null
        ? null
        : Number(item.arquivoVigenteId),
    arquivoNome: toNullableString(item.arquivoNome),
    s3Key: toNullableString(item.s3Key),

    observacao: null,

    versao: 0,
    vigente: Boolean(item.arquivoVigenteId),
    obsoleto: false,
    documentoSubstituidoId: null,
    dataSubstituicao: null,

    portais: {
      totalExigem: toNumber(portais.totalExigem),
      anexadosAtualizados: toNumber(portais.anexadosAtualizados),
      pendenteAnexo: toNumber(portais.pendenteAnexo),
      vencidos: toNumber(portais.vencidos),
      renovacaoPendente: toNumber(
        portais.renovacaoPendente ?? portais.emRenovacao
      ),
      aguardandoValidacao: toNumber(portais.aguardandoValidacao),
    },
  };
}

function getOperacionalVisual(portais: PortalResumo): OperacionalVisual {
  if (!portais.totalExigem || portais.totalExigem <= 0) {
    return {
      key: "nao_exigido",
      label: "Não exigido em portais",
      background: "#f8fafc",
      color: "#475569",
      border: "#cbd5e1",
    };
  }

  if (portais.vencidos > 0) {
    return {
      key: "vencida",
      label: `Vencida em ${portais.vencidos} ${pluralPortal(portais.vencidos)}`,
      background: "#fef2f2",
      color: "#991b1b",
      border: "#fecaca",
    };
  }

  if (portais.pendenteAnexo > 0) {
    return {
      key: "pendente_anexo",
      label: `Pendente anexo em ${portais.pendenteAnexo} ${pluralPortal(
        portais.pendenteAnexo
      )}`,
      background: "#fff7ed",
      color: "#9a3412",
      border: "#fed7aa",
    };
  }

  if (portais.renovacaoPendente > 0) {
    return {
      key: "renovacao_pendente",
      label: `Renovação pendente em ${portais.renovacaoPendente} ${pluralPortal(
        portais.renovacaoPendente
      )}`,
      background: "#eff6ff",
      color: "#1d4ed8",
      border: "#bfdbfe",
    };
  }

  if (portais.aguardandoValidacao > 0) {
    return {
      key: "aguardando_validacao",
      label: `Aguardando validação em ${
        portais.aguardandoValidacao
      } ${pluralPortal(portais.aguardandoValidacao)}`,
      background: "#f5f3ff",
      color: "#6d28d9",
      border: "#ddd6fe",
    };
  }

  if (portais.anexadosAtualizados >= portais.totalExigem) {
    return {
      key: "ok",
      label: `Atualizado em ${portais.totalExigem} ${pluralPortal(
        portais.totalExigem
      )}`,
      background: "#ecfdf3",
      color: "#166534",
      border: "#bbf7d0",
    };
  }

  const pendentesCalculados = Math.max(
    portais.totalExigem - portais.anexadosAtualizados,
    0
  );

  return {
    key: "pendente",
    label: `Pendente em ${pendentesCalculados} ${pluralPortal(
      pendentesCalculados
    )}`,
    background: "#fffbeb",
    color: "#92400e",
    border: "#fde68a",
  };
}

function hasPendenciaOperacional(item: CertidaoItem) {
  const p = item.portais;

  return (
    p.vencidos > 0 ||
    p.pendenteAnexo > 0 ||
    p.renovacaoPendente > 0 ||
    p.aguardandoValidacao > 0 ||
    p.anexadosAtualizados < p.totalExigem ||
    !item.anexoDisponivel
  );
}

function getSectionFromPath(pathname: string): SectionKey {
  const path = pathname.toLowerCase();

  if (path.includes("a-vencer")) return "a-vencer";
  if (path.includes("vencidas")) return "vencidas";
  if (path.includes("anexados")) return "anexados";
  if (path.includes("historico") || path.includes("evidencias")) {
    return "historico";
  }

  return "visao-geral";
}

function getStatusValidadeLabel(status: StatusValidade) {
  switch (status) {
    case "regular":
      return "Regular";
    case "a_vencer":
      return "A vencer";
    case "vencido":
      return "Vencida";
    case "sem_validade":
      return "Sem validade";
    default:
      return "Indefinido";
  }
}

function getPeriodicidadeLabel(periodicidade: Periodicidade) {
  switch (periodicidade) {
    case "semanal":
      return "Semanal";
    case "mensal":
      return "Mensal";
    case "trimestral":
      return "Trimestral";
    case "semestral":
      return "Semestral";
    case "anual":
      return "Anual";
    case "sem_validade":
      return "Sem validade";
    case "sob_demanda":
      return "Sob demanda";
    default:
      return "Indefinida";
  }
}

function getTipoValidadeLabel(tipo: TipoValidade) {
  switch (tipo) {
    case "com_vencimento":
      return "Com vencimento";
    case "sem_vencimento":
      return "Sem vencimento";
    case "sob_demanda":
      return "Sob demanda";
    case "revisao_periodica":
      return "Revisão periódica";
    default:
      return "Indefinido";
  }
}

function getStatusValidadeVisual(status: StatusValidade) {
  switch (status) {
    case "regular":
      return {
        background: "#ecfdf3",
        color: "#166534",
        border: "#bbf7d0",
        icon: <CheckCircle2 size={12} />,
      };
    case "a_vencer":
      return {
        background: "#fffbeb",
        color: "#92400e",
        border: "#fde68a",
        icon: <Clock3 size={12} />,
      };
    case "vencido":
      return {
        background: "#fef2f2",
        color: "#991b1b",
        border: "#fecaca",
        icon: <AlertTriangle size={12} />,
      };
    case "sem_validade":
      return {
        background: "#f8fafc",
        color: "#475569",
        border: "#cbd5e1",
        icon: <Archive size={12} />,
      };
    default:
      return {
        background: "#f8fafc",
        color: "#475569",
        border: "#cbd5e1",
        icon: <FileText size={12} />,
      };
  }
}

function getSectionTitle(section: SectionKey) {
  switch (section) {
    case "a-vencer":
      return "Certidões a vencer";
    case "vencidas":
      return "Certidões vencidas";
    case "anexados":
      return "Documentos anexados";
    case "historico":
      return "Histórico e evidências";
    case "visao-geral":
    default:
      return "Visão geral";
  }
}

function getUniqueOptions(
  items: CertidaoItem[],
  getter: (item: CertidaoItem) => string
) {
  return Array.from(
    new Set(
      items
        .map(getter)
        .map((value) => String(value ?? "").trim())
        .filter(Boolean)
    )
  ).sort((a, b) => a.localeCompare(b, "pt-BR"));
}

function getOperacionalFilterLabel(key: OperacionalKey) {
  switch (key) {
    case "ok":
      return "Atualizado";
    case "pendente_anexo":
      return "Pendente anexo";
    case "vencida":
      return "Vencida";
    case "renovacao_pendente":
      return "Renovação pendente";
    case "aguardando_validacao":
      return "Aguardando validação";
    case "pendente":
      return "Pendente";
    case "nao_exigido":
      return "Não exigido";
    default:
      return "Indefinido";
  }
}

function getEmpresaIdFromSessao(sessao: any): number | null {
  const value =
    sessao?.empresa_id ??
    sessao?.empresaId ??
    sessao?.empresa_atual_id ??
    sessao?.empresaSelecionadaId ??
    sessao?.empresa?.id;

  if (value === undefined || value === null || value === "") {
    return null;
  }

  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function getModuloIdFromSessao(sessao: any): number | null {
  const direct =
    sessao?.modulo_id ??
    sessao?.moduloId ??
    sessao?.modulo_atual_id ??
    sessao?.moduloSelecionadoId ??
    sessao?.modulo?.id;

  if (direct !== undefined && direct !== null && direct !== "") {
    const n = Number(direct);
    return Number.isFinite(n) ? n : null;
  }

  const isGovernancaDepartment = (dep: any) => {
    const depSlug = normalizeText(dep?.slug);
    const depNome = normalizeText(dep?.nome);
    const depRouteKey = normalizeText(dep?.routeKey ?? dep?.route_key);
    const depCodigo = normalizeText(dep?.codigo);

    return (
      depSlug.includes("governanca") ||
      depSlug.includes("governança") ||
      depNome.includes("governanca") ||
      depNome.includes("governança") ||
      depNome.includes("compliance") ||
      depRouteKey.includes("governanca") ||
      depRouteKey.includes("governança") ||
      depCodigo.includes("governanca") ||
      depCodigo.includes("governança")
    );
  };

  const isCertidoesModule = (mod: any) => {
    const id = mod?.id;

    if (id === undefined || id === null || id === "") {
      return false;
    }

    const codigo = normalizeText(mod?.codigo);
    const label = normalizeText(mod?.label);
    const nome = normalizeText(mod?.nome);
    const routeKey = normalizeText(mod?.routeKey ?? mod?.route_key);
    const actionKey = normalizeText(mod?.actionKey ?? mod?.action_key);

    return (
      codigo.includes("certidoes") ||
      codigo.includes("certidões") ||
      codigo.includes("documentos") ||
      codigo.includes("certidoes-e-documentos") ||
      codigo.includes("certidoes_documentos") ||
      label.includes("certidoes") ||
      label.includes("certidões") ||
      label.includes("documentos") ||
      nome.includes("certidoes") ||
      nome.includes("certidões") ||
      nome.includes("documentos") ||
      routeKey.includes("certidoes") ||
      routeKey.includes("certidões") ||
      routeKey.includes("documentos") ||
      actionKey.includes("certidoes") ||
      actionKey.includes("certidões") ||
      actionKey.includes("documentos")
    );
  };

  const extractFromDepartments = (departamentos: any[]): number | null => {
    for (const dep of departamentos) {
      if (!isGovernancaDepartment(dep)) continue;

      const modulos = Array.isArray(dep?.modulos) ? dep.modulos : [];

      const moduloCertidoes =
        modulos.find((mod: any) => isCertidoesModule(mod)) || modulos[0];

      const id = moduloCertidoes?.id;

      if (id !== undefined && id !== null && id !== "") {
        const n = Number(id);
        if (Number.isFinite(n)) return n;
      }
    }

    return null;
  };

  const departamentosDiretos = Array.isArray(sessao?.departamentos_acessiveis)
    ? sessao.departamentos_acessiveis
    : [];

  const fromDepartamentosDiretos = extractFromDepartments(departamentosDiretos);

  if (fromDepartamentosDiretos) {
    return fromDepartamentosDiretos;
  }

  const enderecoSelecionadoId =
    sessao?.endereco_selecionado_id !== undefined &&
    sessao?.endereco_selecionado_id !== null
      ? String(sessao.endereco_selecionado_id)
      : null;

  const enderecosComModulos = Array.isArray(sessao?.enderecos_com_modulos)
    ? sessao.enderecos_com_modulos
    : [];

  if (enderecoSelecionadoId) {
    const enderecoSelecionado = enderecosComModulos.find(
      (end: any) => String(end?.id ?? "") === enderecoSelecionadoId
    );

    const departamentosEndereco = Array.isArray(
      enderecoSelecionado?.departamentos_acessiveis
    )
      ? enderecoSelecionado.departamentos_acessiveis
      : [];

    const fromEnderecoSelecionado = extractFromDepartments(
      departamentosEndereco
    );

    if (fromEnderecoSelecionado) {
      return fromEnderecoSelecionado;
    }
  }

  for (const endereco of enderecosComModulos) {
    const departamentosEndereco = Array.isArray(
      endereco?.departamentos_acessiveis
    )
      ? endereco.departamentos_acessiveis
      : [];

    const fromEndereco = extractFromDepartments(departamentosEndereco);

    if (fromEndereco) {
      return fromEndereco;
    }
  }

  return null;
}

const FilterInput: React.FC<{
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
}> = ({ value, placeholder, onChange }) => {
  return (
    <input
      value={value}
      placeholder={placeholder || "Filtrar"}
      onChange={(event) => onChange(event.target.value)}
      style={styles.filterInput}
    />
  );
};

const FilterSelect: React.FC<{
  value: string;
  placeholder?: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
}> = ({ value, placeholder, options, onChange }) => {
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      style={styles.filterSelect}
    >
      <option value="">{placeholder || "Todos"}</option>

      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
};

const RegisterDocumentOverlay: React.FC<{
  open: boolean;
  departmentColor: string;
  form: RegisterDocumentForm;
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onChange: <K extends keyof RegisterDocumentForm>(
    key: K,
    value: RegisterDocumentForm[K]
  ) => void;
  onSubmit: () => void;
}> = ({
  open,
  departmentColor,
  form,
  saving,
  error,
  onClose,
  onChange,
  onSubmit,
}) => {
  if (!open) return null;

  const nomeValue = String(form.nome ?? "");
  const categoriaValue = String(form.categoria ?? "");
  const dataValidadeValue = String(form.dataValidade ?? "");

  const hasRequiredBase =
    nomeValue.trim().length > 0 && categoriaValue.trim().length > 0;

  const hasFileDates =
    !form.file ||
    form.tipoValidade === "sem_vencimento" ||
    form.tipoValidade === "sob_demanda" ||
    dataValidadeValue.trim().length > 0;

  const canSubmit = hasRequiredBase && hasFileDates && !saving;

  return (
    <div style={styles.overlayBackdrop}>
      <div style={styles.overlayPanel}>
        <div style={styles.overlayHeader}>
          <div>
            <div style={styles.overlayEyebrow}>Governança documental</div>
            <h2 style={styles.overlayTitle}>Registrar documento</h2>
            <p style={styles.overlaySubtitle}>
              Cadastre o documento, anexe o arquivo vigente e informe emissão e
              validade quando aplicável.
            </p>
          </div>

          <button
            type="button"
            style={styles.overlayCloseButton}
            onClick={onClose}
            disabled={saving}
          >
            <X size={17} />
          </button>
        </div>

        {error && (
          <div style={styles.overlayError}>
            <AlertTriangle size={15} />
            <span>{error}</span>
          </div>
        )}

        <div style={styles.formGrid}>
          <div style={styles.formFieldWide}>
            <label style={styles.formLabel}>Documento *</label>
            <input
              style={styles.formInput}
              value={form.nome}
              onChange={(event) => onChange("nome", event.target.value)}
              placeholder="Ex.: Certificado de Regularidade do FGTS"
              disabled={saving}
            />
          </div>

          <div style={styles.formField}>
            <label style={styles.formLabel}>Categoria *</label>
            <input
              style={styles.formInput}
              value={form.categoria}
              onChange={(event) => onChange("categoria", event.target.value)}
              placeholder="Ex.: Trabalhista, Fiscal, Societário"
              disabled={saving}
            />
          </div>

          <div style={styles.formField}>
            <label style={styles.formLabel}>Órgão emissor</label>
            <input
              style={styles.formInput}
              value={form.orgaoEmissor}
              onChange={(event) => onChange("orgaoEmissor", event.target.value)}
              placeholder="Ex.: Caixa Econômica Federal"
              disabled={saving}
            />
          </div>

          <div style={styles.formField}>
            <label style={styles.formLabel}>Tipo de validade</label>
            <select
              style={styles.formInput}
              value={form.tipoValidade}
              onChange={(event) =>
                onChange("tipoValidade", event.target.value as TipoValidade)
              }
              disabled={saving}
            >
              <option value="com_vencimento">Com vencimento</option>
              <option value="sem_vencimento">Sem vencimento</option>
              <option value="sob_demanda">Sob demanda</option>
              <option value="revisao_periodica">Revisão periódica</option>
            </select>
          </div>

          <div style={styles.formField}>
            <label style={styles.formLabel}>Periodicidade</label>
            <select
              style={styles.formInput}
              value={form.periodicidade}
              onChange={(event) =>
                onChange("periodicidade", event.target.value as Periodicidade)
              }
              disabled={saving}
            >
              <option value="sob_demanda">Sob demanda</option>
              <option value="semanal">Semanal</option>
              <option value="mensal">Mensal</option>
              <option value="trimestral">Trimestral</option>
              <option value="semestral">Semestral</option>
              <option value="anual">Anual</option>
              <option value="sem_validade">Sem validade</option>
            </select>
          </div>

          <div style={styles.formField}>
            <label style={styles.formLabel}>Área responsável</label>
            <input
              style={styles.formInput}
              value={form.areaResponsavel}
              onChange={(event) =>
                onChange("areaResponsavel", event.target.value)
              }
              placeholder="Ex.: Departamento Pessoal"
              disabled={saving}
            />
          </div>

          <div style={styles.formField}>
            <label style={styles.formLabel}>Responsável</label>
            <input
              style={styles.formInput}
              value={form.responsavelNome}
              onChange={(event) =>
                onChange("responsavelNome", event.target.value)
              }
              placeholder="Ex.: DP / Contabilidade"
              disabled={saving}
            />
          </div>

          <div style={styles.formFieldFull}>
            <div style={styles.formDivider}>
              <Paperclip size={14} />
              <span>Anexo vigente</span>
            </div>
          </div>

          <div style={styles.formFieldFull}>
            <label style={styles.uploadBox}>
              <div style={styles.uploadBoxIcon}>
                <Paperclip size={17} />
              </div>

              <div style={styles.uploadBoxText}>
                <strong>
                  {form.file ? form.file.name : "Selecionar documento"}
                </strong>
                <span>
                  PDF, PNG, JPG ou JPEG. O arquivo será enviado ao S3 após o
                  cadastro do documento.
                </span>
              </div>

              <input
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                style={styles.hiddenFileInput}
                disabled={saving}
                onChange={(event) => {
                  const file = event.target.files?.[0] || null;
                  onChange("file", file);
                }}
              />
            </label>
          </div>

          <div style={styles.formField}>
            <label style={styles.formLabel}>Data de emissão</label>
            <input
              type="date"
              style={styles.formInput}
              value={form.dataEmissao}
              onChange={(event) => onChange("dataEmissao", event.target.value)}
              disabled={saving || !form.file}
            />
          </div>

          <div style={styles.formField}>
            <label style={styles.formLabel}>
              Data de vencimento{" "}
              {form.file &&
              form.tipoValidade !== "sem_vencimento" &&
              form.tipoValidade !== "sob_demanda"
                ? "*"
                : ""}
            </label>
            <input
              type="date"
              style={styles.formInput}
              value={form.dataValidade}
              onChange={(event) => onChange("dataValidade", event.target.value)}
              disabled={
                saving ||
                !form.file ||
                form.tipoValidade === "sem_vencimento" ||
                form.tipoValidade === "sob_demanda"
              }
            />
          </div>

          <div style={styles.formFieldFull}>
            <label style={styles.formLabel}>Observação do anexo</label>
            <textarea
              style={styles.formTextareaSmall}
              value={form.uploadObservacao}
              onChange={(event) =>
                onChange("uploadObservacao", event.target.value)
              }
              placeholder="Ex.: Documento emitido no portal oficial e anexado como versão vigente."
              disabled={saving || !form.file}
            />
          </div>

          <div style={styles.formFieldFull}>
            <label style={styles.formLabel}>Descrição / observação geral</label>
            <textarea
              style={styles.formTextarea}
              value={form.descricao}
              onChange={(event) => onChange("descricao", event.target.value)}
              placeholder="Descrição breve sobre a finalidade, exigência ou regra de controle deste documento."
              disabled={saving}
            />
          </div>
        </div>

        <div style={styles.overlayFooter}>
          <button
            type="button"
            style={styles.cancelButton}
            onClick={onClose}
            disabled={saving}
          >
            Cancelar
          </button>

          <button
            type="button"
            style={{
              ...styles.saveButton,
              background: canSubmit ? departmentColor : "#94a3b8",
              borderColor: canSubmit ? departmentColor : "#94a3b8",
            }}
            onClick={onSubmit}
            disabled={!canSubmit}
          >
            {saving ? <Loader2 size={15} /> : <Save size={15} />}
            {form.file ? "Registrar e anexar" : "Registrar documento"}
          </button>
        </div>
      </div>
    </div>
  );
};

const CertidoesDocumentosLayout: React.FC<CertidoesDocumentosLayoutProps> = ({
  section,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const { sessao } = useAuth();

  const { departmentColor } = useTheme();
  const activeDepartmentColor = departmentColor || DEPARTMENT_COLOR;

  const [filters, setFilters] = useState<ColumnFilters>(initialFilters);
  const [documentos, setDocumentos] = useState<CertidaoItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const [registerOpen, setRegisterOpen] = useState(false);
  const [registerSaving, setRegisterSaving] = useState(false);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [registerForm, setRegisterForm] =
    useState<RegisterDocumentForm>(initialRegisterForm);

  const activeSection = section || getSectionFromPath(location.pathname);

  const empresaId = useMemo(() => getEmpresaIdFromSessao(sessao), [sessao]);
  const moduloId = useMemo(() => getModuloIdFromSessao(sessao), [sessao]);

  const empresaNome =
    sessao?.empresa?.razao_social ||
    sessao?.empresa_razao_social ||
    "Empresa não informada";

  const empresaCnpj = sessao?.empresa?.cnpj || sessao?.empresa_cnpj || "";

  const updateFilter = (key: keyof ColumnFilters, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const updateRegisterForm = <K extends keyof RegisterDocumentForm>(
    key: K,
    value: RegisterDocumentForm[K]
  ) => {
    setRegisterForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const openRegisterOverlay = () => {
    setRegisterError(null);
    setRegisterOpen(true);
  };

  const closeRegisterOverlay = () => {
    if (registerSaving) return;

    setRegisterOpen(false);
    setRegisterError(null);
  };

  const resetRegisterForm = () => {
    setRegisterForm(initialRegisterForm);
  };

  const reloadDocumentos = () => {
    setReloadKey((prev) => prev + 1);
  };

  const handleRegisterDocument = async () => {
    if (registerSaving) return;

    setRegisterError(null);

    try {
      if (!empresaId) {
        throw new Error(
          "empresa_id não encontrado no contexto de autenticação."
        );
      }

      if (!moduloId) {
        throw new Error(
          "modulo_id não encontrado no contexto. Verifique o vínculo do módulo no menu/permissões."
        );
      }

      const nomeFinal = String(registerForm.nome ?? "").trim();
      const categoriaFinal = String(registerForm.categoria ?? "").trim();

      if (!nomeFinal) {
        throw new Error("Informe o nome do documento.");
      }

      if (!categoriaFinal) {
        throw new Error("Informe a categoria do documento.");
      }

      if (
        registerForm.file &&
        registerForm.tipoValidade !== "sem_vencimento" &&
        registerForm.tipoValidade !== "sob_demanda" &&
        !String(registerForm.dataValidade ?? "").trim()
      ) {
        throw new Error("Informe a data de vencimento do documento anexado.");
      }

      setRegisterSaving(true);

      const documentoResponse = await fetch(API_DOCUMENTOS_URL, {
        method: "POST",
        credentials: "include",
        cache: "no-store",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          empresa_id: empresaId,
          modulo_id: moduloId,
          nome: nomeFinal,
          categoria: categoriaFinal,
          orgao_emissor: String(registerForm.orgaoEmissor ?? "").trim() || null,
          descricao: String(registerForm.descricao ?? "").trim() || null,
          area_responsavel:
            String(registerForm.areaResponsavel ?? "").trim() || null,
          responsavel_nome:
            String(registerForm.responsavelNome ?? "").trim() || null,
          tipo_validade: registerForm.tipoValidade,
          periodicidade: registerForm.periodicidade,
        }),
      });

      const documentoData = (await documentoResponse
        .json()
        .catch(() => null)) as ApiPostDocumentoResponse | null;

      if (!documentoResponse.ok || !documentoData?.ok) {
        throw new Error(
          documentoData?.erro ||
            `Falha ao registrar documento. Status HTTP ${documentoResponse.status}`
        );
      }

      const documentoId = Number(documentoData?.documento?.id);

      if (!Number.isFinite(documentoId)) {
        throw new Error(
          "Documento criado, mas o ID não foi retornado pela API."
        );
      }

      if (registerForm.file) {
        const file = registerForm.file;

        const presignResponse = await fetch(API_PRESIGN_UPLOAD_URL, {
          method: "POST",
          credentials: "include",
          cache: "no-store",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            empresa_id: empresaId,
            modulo_id: moduloId,
            documento_id: documentoId,
            mime_type: file.type || "application/octet-stream",
            original_name: file.name,
            size_bytes: file.size,
          }),
        });

        const presignData = (await presignResponse
          .json()
          .catch(() => null)) as ApiPresignUploadResponse | null;

        if (!presignResponse.ok || !presignData?.ok || !presignData.uploadUrl) {
          throw new Error(
            presignData?.erro ||
              `Documento criado, mas falhou ao preparar upload. Status HTTP ${presignResponse.status}`
          );
        }

        const uploadResponse = await fetch(presignData.uploadUrl, {
          method: "PUT",
          headers: {
            "Content-Type": file.type || "application/octet-stream",
          },
          body: file,
        });

        if (!uploadResponse.ok) {
          throw new Error(
            `Documento criado, mas falhou ao enviar arquivo ao S3. Status HTTP ${uploadResponse.status}`
          );
        }

        const registrarResponse = await fetch(API_REGISTRAR_ANEXO_URL, {
          method: "POST",
          credentials: "include",
          cache: "no-store",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            empresa_id: empresaId,
            modulo_id: moduloId,
            documento_id: documentoId,
            s3_bucket: presignData.bucket,
            s3_key: presignData.key,
            mime_type: file.type || "application/octet-stream",
            original_name: file.name,
            size_bytes: file.size,
            data_emissao: registerForm.dataEmissao || null,
            data_validade:
              registerForm.tipoValidade === "sem_vencimento" ||
              registerForm.tipoValidade === "sob_demanda"
                ? null
                : registerForm.dataValidade || null,
            observacao:
              String(registerForm.uploadObservacao ?? "").trim() || null,
          }),
        });

        const registrarData = (await registrarResponse
          .json()
          .catch(() => null)) as ApiRegistrarAnexoResponse | null;

        if (!registrarResponse.ok || !registrarData?.ok) {
          throw new Error(
            registrarData?.erro ||
              `Documento criado, mas falhou ao registrar o anexo. Status HTTP ${registrarResponse.status}`
          );
        }
      }

      resetRegisterForm();
      setRegisterOpen(false);
      reloadDocumentos();
    } catch (error: any) {
      setRegisterError(error?.message || "Falha ao registrar documento.");
    } finally {
      setRegisterSaving(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const carregarDocumentos = async () => {
      setLoading(true);
      setErro(null);

      try {
        if (!empresaId) {
          throw new Error(
            "empresa_id não encontrado no contexto de autenticação."
          );
        }

        if (!moduloId) {
          throw new Error(
            "modulo_id não encontrado no contexto. Verifique se o módulo Certidões e Documentos está vinculado ao departamento Governança no menu/permissões."
          );
        }

        const params = new URLSearchParams();

        params.set("section", activeSection);
        params.set("empresa_id", String(empresaId));
        params.set("modulo_id", String(moduloId));

        const url = `${API_DOCUMENTOS_URL}?${params.toString()}`;

        const response = await fetch(url, {
          method: "GET",
          credentials: "include",
          cache: "no-store",
          headers: {
            Accept: "application/json",
          },
        });

        const data = (await response
          .json()
          .catch(() => null)) as ApiDocumentosResponse | null;

        if (!response.ok || !data?.ok) {
          throw new Error(
            data?.erro ||
              `Falha ao carregar documentos. Status HTTP ${response.status}`
          );
        }

        const items = Array.isArray(data.items) ? data.items : [];

        const mapped = items.map((item) =>
          mapApiItemToCertidaoItem(item, empresaNome, empresaCnpj)
        );

        if (!cancelled) {
          setDocumentos(mapped);
        }
      } catch (error: any) {
        if (!cancelled) {
          setErro(error?.message || "Falha ao carregar documentos.");
          setDocumentos([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    carregarDocumentos();

    return () => {
      cancelled = true;
    };
  }, [activeSection, empresaId, moduloId, empresaNome, empresaCnpj, reloadKey]);

  const filteredBySection = useMemo(() => {
    if (activeSection === "a-vencer") {
      return documentos.filter((item) => item.statusValidade === "a_vencer");
    }

    if (activeSection === "vencidas") {
      return documentos.filter((item) => item.statusValidade === "vencido");
    }

    if (activeSection === "anexados") {
      return documentos.filter((item) => item.anexoDisponivel);
    }

    if (activeSection === "historico") {
      return documentos.filter(
        (item) =>
          item.obsoleto ||
          item.versao > 1 ||
          hasPendenciaOperacional(item) ||
          item.anexoDisponivel
      );
    }

    return documentos;
  }, [activeSection, documentos]);

  const filterOptions = useMemo(() => {
    const categorias = getUniqueOptions(
      filteredBySection,
      (item) => item.categoria
    );

    const orgaos = getUniqueOptions(
      filteredBySection,
      (item) => item.orgaoEmissor
    );

    const responsaveis = getUniqueOptions(
      filteredBySection,
      (item) => item.areaResponsavel
    );

    const regras = Array.from(
      new Map(
        filteredBySection.map((item) => [
          item.tipoValidade,
          getTipoValidadeLabel(item.tipoValidade),
        ])
      )
    )
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));

    const statusValidade = Array.from(
      new Map(
        filteredBySection.map((item) => [
          item.statusValidade,
          getStatusValidadeLabel(item.statusValidade),
        ])
      )
    )
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));

    const operacional = Array.from(
      new Map(
        filteredBySection.map((item) => {
          const visual = getOperacionalVisual(item.portais);
          return [visual.key, getOperacionalFilterLabel(visual.key)];
        })
      )
    )
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));

    return {
      categorias: categorias.map((value) => ({ value, label: value })),
      orgaos: orgaos.map((value) => ({ value, label: value })),
      regras,
      responsaveis: responsaveis.map((value) => ({ value, label: value })),
      statusValidade,
      operacional,
      anexos: [
        { value: "vinculado", label: "Vinculado" },
        { value: "pendente", label: "Pendente" },
      ],
    };
  }, [filteredBySection]);

  const filteredItems = useMemo(() => {
    return filteredBySection.filter((item) => {
      const operacional = getOperacionalVisual(item.portais);

      const documentoSearch = normalizeText(filters.documento);
      const validadeSearch = normalizeText(filters.validade);

      const documentoMatch =
        !documentoSearch ||
        [
          item.nome,
          item.cnpj,
          item.empresa,
          item.arquivoNome || "",
          item.s3Key || "",
          item.obsoleto ? "obsoleto" : "",
        ].some((field) => normalizeText(field).includes(documentoSearch));

      const categoriaMatch =
        !filters.categoria || item.categoria === filters.categoria;

      const orgaoMatch =
        !filters.orgaoEmissor || item.orgaoEmissor === filters.orgaoEmissor;

      const validadeMatch =
        !validadeSearch ||
        [
          item.dataValidade || "Não informada",
          getPrazoOperacional(item),
          item.dataEmissao
            ? `Emissão: ${item.dataEmissao}`
            : "Emissão: Não informada",
        ].some((field) => normalizeText(field).includes(validadeSearch));

      const regraMatch = !filters.regra || item.tipoValidade === filters.regra;

      const responsavelMatch =
        !filters.responsavel || item.areaResponsavel === filters.responsavel;

      const statusValidadeMatch =
        !filters.statusValidade ||
        item.statusValidade === filters.statusValidade;

      const operacionalMatch =
        !filters.operacional || operacional.key === filters.operacional;

      const anexoValue: AnexoFilter = item.anexoDisponivel
        ? "vinculado"
        : "pendente";

      const anexoMatch = !filters.anexo || anexoValue === filters.anexo;

      return (
        documentoMatch &&
        categoriaMatch &&
        orgaoMatch &&
        validadeMatch &&
        regraMatch &&
        responsavelMatch &&
        statusValidadeMatch &&
        operacionalMatch &&
        anexoMatch
      );
    });
  }, [filteredBySection, filters]);

  return (
    <div style={styles.page}>
      <TitleButtons
        fullBleedLine
        title={`Certidões e Documentos - ${getSectionTitle(activeSection)}`}
      />

      <div style={styles.content}>
        <section style={styles.navigationBar}>
          <nav style={styles.tabs}>
            {sections.map((item) => {
              const active = item.key === activeSection;

              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => navigate(item.path)}
                  style={{
                    ...styles.tabButton,
                    ...(active ? styles.tabButtonActive : {}),
                    ...(active
                      ? {
                          background: toRgba(activeDepartmentColor, 0.08),
                          borderColor: activeDepartmentColor,
                          color: activeDepartmentColor,
                        }
                      : {}),
                  }}
                >
                  <span style={styles.tabIcon}>{item.icon}</span>
                  {item.label}
                </button>
              );
            })}
          </nav>

          <button
            type="button"
            style={{
              ...styles.registerButton,
              color: "#ffffff",
              borderColor: activeDepartmentColor,
              background: activeDepartmentColor,
            }}
            onClick={openRegisterOverlay}
          >
            <Plus size={14} />
            Registrar documento
          </button>
        </section>

        {(activeSection === "a-vencer" || activeSection === "vencidas") && (
          <section
            style={{
              ...styles.notice,
              ...(activeSection === "vencidas"
                ? styles.noticeDanger
                : styles.noticeWarning),
            }}
          >
            <div style={styles.noticeIcon}>
              {activeSection === "vencidas" ? (
                <AlertTriangle size={16} />
              ) : (
                <CalendarClock size={16} />
              )}
            </div>

            <div>
              <div style={styles.noticeTitle}>
                {activeSection === "vencidas"
                  ? "Regularização necessária"
                  : "Renovação preventiva recomendada"}
              </div>

              <div style={styles.noticeText}>
                {activeSection === "vencidas"
                  ? "Existem documentos vencidos. Recomenda-se tratar a regularização como prioridade operacional."
                  : "Documentos próximos ao vencimento devem ser acompanhados com antecedência para evitar perda de regularidade."}
              </div>
            </div>
          </section>
        )}

        {erro && (
          <section style={styles.errorBox}>
            <AlertTriangle size={16} />
            <div>
              <strong>Não foi possível carregar os documentos.</strong>
              <span>{erro}</span>
            </div>
          </section>
        )}

        <section style={styles.tablePanel}>
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Documento</th>
                  <th style={styles.th}>Categoria</th>
                  <th style={styles.th}>Órgão emissor</th>
                  <th style={styles.th}>Validade</th>
                  <th style={styles.th}>Regra</th>
                  <th style={styles.th}>Responsável</th>
                  <th style={styles.th}>Status validade</th>
                  <th style={styles.th}>Operacional</th>
                  <th style={styles.th}>Anexo</th>
                  <th style={styles.th}>Pendência</th>
                </tr>

                <tr>
                  <th style={styles.filterTh}>
                    <FilterInput
                      value={filters.documento}
                      placeholder="Documento"
                      onChange={(value) => updateFilter("documento", value)}
                    />
                  </th>

                  <th style={styles.filterTh}>
                    <FilterSelect
                      value={filters.categoria}
                      placeholder="Todos"
                      options={filterOptions.categorias}
                      onChange={(value) => updateFilter("categoria", value)}
                    />
                  </th>

                  <th style={styles.filterTh}>
                    <FilterSelect
                      value={filters.orgaoEmissor}
                      placeholder="Todos"
                      options={filterOptions.orgaos}
                      onChange={(value) => updateFilter("orgaoEmissor", value)}
                    />
                  </th>

                  <th style={styles.filterTh}>
                    <FilterInput
                      value={filters.validade}
                      placeholder="Validade"
                      onChange={(value) => updateFilter("validade", value)}
                    />
                  </th>

                  <th style={styles.filterTh}>
                    <FilterSelect
                      value={filters.regra}
                      placeholder="Todos"
                      options={filterOptions.regras}
                      onChange={(value) => updateFilter("regra", value)}
                    />
                  </th>

                  <th style={styles.filterTh}>
                    <FilterSelect
                      value={filters.responsavel}
                      placeholder="Todos"
                      options={filterOptions.responsaveis}
                      onChange={(value) => updateFilter("responsavel", value)}
                    />
                  </th>

                  <th style={styles.filterTh}>
                    <FilterSelect
                      value={filters.statusValidade}
                      placeholder="Todos"
                      options={filterOptions.statusValidade}
                      onChange={(value) =>
                        updateFilter("statusValidade", value)
                      }
                    />
                  </th>

                  <th style={styles.filterTh}>
                    <FilterSelect
                      value={filters.operacional}
                      placeholder="Todos"
                      options={filterOptions.operacional}
                      onChange={(value) => updateFilter("operacional", value)}
                    />
                  </th>

                  <th style={styles.filterTh}>
                    <FilterSelect
                      value={filters.anexo}
                      placeholder="Todos"
                      options={filterOptions.anexos}
                      onChange={(value) => updateFilter("anexo", value)}
                    />
                  </th>

                  <th style={styles.filterTh}>
                    <div style={styles.filterDisabled} />
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={10} style={styles.loadingCell}>
                      <Loader2 size={16} />
                      Carregando documentos...
                    </td>
                  </tr>
                )}

                {!loading &&
                  filteredItems.map((item) => (
                    <DocumentRow
                      key={item.id}
                      item={item}
                      departmentColor={activeDepartmentColor}
                      onDoubleClick={() =>
                        navigate(`${BASE_PATH}/documento/${item.id}`)
                      }
                    />
                  ))}

                {!loading && filteredItems.length === 0 && (
                  <tr>
                    <td colSpan={10} style={styles.emptyCell}>
                      Nenhum documento registrado.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <RegisterDocumentOverlay
        open={registerOpen}
        departmentColor={activeDepartmentColor}
        form={registerForm}
        saving={registerSaving}
        error={registerError}
        onClose={closeRegisterOverlay}
        onChange={updateRegisterForm}
        onSubmit={handleRegisterDocument}
      />
    </div>
  );
};

const DocumentRow: React.FC<{
  item: CertidaoItem;
  departmentColor: string;
  onDoubleClick?: () => void;
}> = ({ item, departmentColor, onDoubleClick }) => {
  const [isHovered, setIsHovered] = useState(false);

  const statusValidadeVisual = getStatusValidadeVisual(item.statusValidade);
  const operacional = getOperacionalVisual(item.portais);

  const rowHoverBackground = toRgba(departmentColor, 0.055);
  const rowHoverBorder = toRgba(departmentColor, 0.22);
  const rowHoverIconBackground = toRgba(departmentColor, 0.08);
  const rowHoverIconBorder = toRgba(departmentColor, 0.28);

  const tdStyle: React.CSSProperties = {
    ...styles.td,
    borderTopColor: isHovered ? rowHoverBorder : "#f0f2f4",
    borderBottomColor: isHovered ? rowHoverBorder : "#f0f2f4",
  };

  return (
    <tr
      style={{
        ...styles.tr,
        background: isHovered ? rowHoverBackground : "#ffffff",
        boxShadow: isHovered
          ? `inset 3px 0 0 ${departmentColor}`
          : "inset 0 0 0 transparent",
        transition:
          "background 140ms ease, box-shadow 140ms ease, border-color 140ms ease",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onDoubleClick={onDoubleClick}
      title="Clique duas vezes para abrir os detalhes do documento"
    >
      <td style={tdStyle}>
        <div style={styles.docCell}>
          <div
            style={{
              ...styles.docIcon,
              ...(isHovered
                ? {
                    background: rowHoverIconBackground,
                    borderColor: rowHoverIconBorder,
                    color: departmentColor,
                  }
                : {}),
            }}
          >
            <FileText size={14} />
          </div>

          <div style={styles.docInfo}>
            <div
              style={{
                ...styles.docName,
                color: isHovered ? departmentColor : "#111827",
              }}
            >
              {item.nome}
            </div>

            <div style={styles.docMeta}>
              {item.cnpj || item.empresa}
              {item.arquivoNome ? ` • ${item.arquivoNome}` : ""}
              {item.obsoleto ? " • Obsoleto" : ""}
            </div>
          </div>
        </div>
      </td>

      <td style={tdStyle}>{item.categoria}</td>

      <td style={tdStyle}>{item.orgaoEmissor}</td>

      <td style={tdStyle}>
        <div style={styles.dateText}>
          {item.dataValidade || "Não informada"}
        </div>

        <div
          style={{
            ...styles.prazoText,
            ...(item.statusValidade === "vencido" ? styles.prazoDanger : {}),
            ...(item.statusValidade === "a_vencer" ? styles.prazoWarning : {}),
          }}
        >
          {getPrazoOperacional(item)}
        </div>

        <div style={styles.docMeta}>
          Emissão: {item.dataEmissao || "Não informada"}
        </div>
      </td>

      <td style={tdStyle}>
        <div style={styles.ruleText}>
          {getTipoValidadeLabel(item.tipoValidade)}
        </div>

        <div style={styles.docMeta}>
          {getPeriodicidadeLabel(item.periodicidade)}
        </div>
      </td>

      <td style={tdStyle}>
        <div style={styles.responsavelArea}>{item.areaResponsavel}</div>

        {item.responsavelNome && (
          <div style={styles.docMeta}>{item.responsavelNome}</div>
        )}
      </td>

      <td style={tdStyle}>
        <span
          style={{
            ...styles.statusBadge,
            background: statusValidadeVisual.background,
            color: statusValidadeVisual.color,
            borderColor: statusValidadeVisual.border,
          }}
        >
          {statusValidadeVisual.icon}
          {getStatusValidadeLabel(item.statusValidade)}
        </span>
      </td>

      <td style={tdStyle}>
        <span
          style={{
            ...styles.statusBadge,
            background: operacional.background,
            color: operacional.color,
            borderColor: operacional.border,
          }}
        >
          {operacional.label}
        </span>
      </td>

      <td style={tdStyle}>
        {item.anexoDisponivel ? (
          <span style={styles.attachmentOk}>
            <Paperclip size={12} />
            Vinculado
          </span>
        ) : (
          <span style={styles.attachmentPending}>Pendente</span>
        )}
      </td>

      <td style={tdStyle}>
        {item.observacao ? (
          <span style={styles.observacaoText}>{item.observacao}</span>
        ) : hasPendenciaOperacional(item) ? (
          <span style={styles.observacaoText}>Ação operacional pendente</span>
        ) : (
          <span style={styles.docMeta}>Sem pendência</span>
        )}
      </td>
    </tr>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    width: "100%",
    color: "#111827",
    fontFamily:
      '"Nunito", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },

  content: {
    marginTop: 12,
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },

  navigationBar: {
    minHeight: 42,
    background: "#ffffff",
    border: "1px solid #dfe3e8",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "5px 8px",
  },

  tabs: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    overflowX: "auto",
    minWidth: 0,
  },

  tabButton: {
    height: 30,
    padding: "0 10px",
    border: "1px solid transparent",
    background: "transparent",
    color: "#64748b",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    whiteSpace: "nowrap",
    transition:
      "background 140ms ease, color 140ms ease, border-color 140ms ease",
  },

  tabButtonActive: {
    background: "rgba(47, 63, 88, 0.08)",
    borderColor: DEPARTMENT_COLOR,
    color: DEPARTMENT_COLOR,
  },

  tabIcon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "inherit",
  },

  registerButton: {
    height: 30,
    padding: "0 11px",
    border: "1px solid",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 11.8,
    fontWeight: 850,
    cursor: "pointer",
    fontFamily: "inherit",
    whiteSpace: "nowrap",
  },

  notice: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    padding: "10px 12px",
    border: "1px solid",
  },

  noticeWarning: {
    background: "#fff8e8",
    borderColor: "#e7cf9a",
    color: "#6b4f1f",
  },

  noticeDanger: {
    background: "#fff1f1",
    borderColor: "#efb4b4",
    color: "#7f1d1d",
  },

  noticeIcon: {
    marginTop: 1,
    display: "inline-flex",
    flexShrink: 0,
  },

  noticeTitle: {
    fontSize: 13,
    fontWeight: 850,
    marginBottom: 2,
  },

  noticeText: {
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.35,
  },

  errorBox: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    padding: "10px 12px",
    border: "1px solid #fecaca",
    background: "#fef2f2",
    color: "#991b1b",
    fontSize: 12,
    fontWeight: 700,
  },

  tablePanel: {
    background: "#ffffff",
    border: "1px solid #dfe3e8",
    overflow: "hidden",
  },

  tableWrap: {
    width: "100%",
    overflowX: "auto",
  },

  table: {
    width: "100%",
    minWidth: 1320,
    borderCollapse: "collapse",
    tableLayout: "fixed",
  },

  th: {
    width: "10%",
    padding: "8px 10px",
    background: "#f4f6f8",
    borderBottom: "1px solid #dfe3e8",
    borderRight: "1px solid #e6e9ed",
    textAlign: "left",
    fontSize: 11,
    fontWeight: 900,
    color: "#3f4752",
    textTransform: "uppercase",
    letterSpacing: 0.2,
    whiteSpace: "nowrap",
  },

  filterTh: {
    width: "10%",
    padding: "5px 6px",
    background: "#ffffff",
    borderBottom: "1px solid #dfe3e8",
    borderRight: "1px solid #e6e9ed",
    textAlign: "left",
  },

  filterInput: {
    width: "100%",
    height: 28,
    boxSizing: "border-box",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#111827",
    padding: "0 7px",
    fontSize: 11.5,
    fontWeight: 650,
    outline: "none",
    fontFamily: "inherit",
  },

  filterSelect: {
    width: "100%",
    height: 28,
    boxSizing: "border-box",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#111827",
    padding: "0 6px",
    fontSize: 11.5,
    fontWeight: 650,
    outline: "none",
    fontFamily: "inherit",
  },

  filterDisabled: {
    width: "100%",
    height: 28,
    boxSizing: "border-box",
    border: "1px solid #e5e7eb",
    background: "#f8fafc",
  },

  tr: {
    borderBottom: "1px solid #edf0f2",
    cursor: "pointer",
    background: "#ffffff",
  },

  td: {
    padding: "8px 10px",
    fontSize: 12.2,
    fontWeight: 650,
    color: "#334155",
    verticalAlign: "middle",
    borderRight: "1px solid #f0f2f4",
    overflow: "hidden",
    transition:
      "background 140ms ease, color 140ms ease, border-color 140ms ease",
  },

  docCell: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    minWidth: 0,
  },

  docIcon: {
    width: 28,
    height: 28,
    background: "#f2f5f8",
    border: "1px solid #e2e8f0",
    color: DEPARTMENT_COLOR,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  docInfo: {
    minWidth: 0,
  },

  docName: {
    fontSize: 12.4,
    fontWeight: 850,
    color: "#111827",
    lineHeight: 1.25,
  },

  docMeta: {
    marginTop: 1,
    fontSize: 11,
    fontWeight: 600,
    color: "#64748b",
  },

  dateText: {
    fontSize: 12.2,
    fontWeight: 800,
    color: "#111827",
  },

  prazoText: {
    marginTop: 1,
    fontSize: 11.4,
    fontWeight: 800,
    color: "#166534",
  },

  prazoWarning: {
    color: "#92400e",
  },

  prazoDanger: {
    color: "#991b1b",
  },

  ruleText: {
    fontSize: 12.2,
    fontWeight: 800,
    color: "#334155",
  },

  responsavelArea: {
    fontSize: 12.2,
    fontWeight: 850,
    color: "#334155",
  },

  statusBadge: {
    minHeight: 22,
    padding: "0 8px",
    border: "1px solid",
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 11,
    fontWeight: 850,
    whiteSpace: "nowrap",
  },

  attachmentOk: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    color: "#166534",
    fontSize: 11.8,
    fontWeight: 850,
  },

  attachmentPending: {
    color: "#92400e",
    fontSize: 11.8,
    fontWeight: 850,
  },

  observacaoText: {
    display: "inline-block",
    maxWidth: 230,
    color: "#6b4f1f",
    fontSize: 11.5,
    fontWeight: 700,
    lineHeight: 1.3,
  },

  loadingCell: {
    padding: 26,
    textAlign: "center",
    color: "#64748b",
    fontSize: 13,
    fontWeight: 800,
  },

  emptyCell: {
    padding: 26,
    textAlign: "center",
    color: "#64748b",
    fontSize: 13,
    fontWeight: 700,
  },

  overlayBackdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    background: "rgba(15, 23, 42, 0.42)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 18,
  },

  overlayPanel: {
    width: "min(760px, 100%)",
    maxHeight: "calc(100vh - 36px)",
    overflowY: "auto",
    background: "#ffffff",
    border: "1px solid #dfe3e8",
    boxShadow: "0 24px 70px rgba(15, 23, 42, 0.28)",
  },

  overlayHeader: {
    padding: "16px 18px 14px",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    justifyContent: "space-between",
    gap: 18,
    alignItems: "flex-start",
  },

  overlayEyebrow: {
    fontSize: 11,
    fontWeight: 900,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 4,
  },

  overlayTitle: {
    margin: 0,
    fontSize: 18,
    fontWeight: 950,
    color: "#111827",
    lineHeight: 1.2,
  },

  overlaySubtitle: {
    margin: "5px 0 0",
    fontSize: 12.4,
    fontWeight: 650,
    color: "#64748b",
    lineHeight: 1.35,
  },

  overlayCloseButton: {
    width: 32,
    height: 32,
    border: "1px solid #d8dde5",
    background: "#ffffff",
    color: "#475569",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  overlayError: {
    margin: "12px 18px 0",
    border: "1px solid #fecaca",
    background: "#fef2f2",
    color: "#991b1b",
    padding: "9px 10px",
    display: "flex",
    alignItems: "center",
    gap: 7,
    fontSize: 12,
    fontWeight: 800,
  },

  formGrid: {
    padding: 18,
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 12,
  },

  formField: {
    display: "flex",
    flexDirection: "column",
    gap: 5,
    minWidth: 0,
  },

  formFieldWide: {
    gridColumn: "1 / -1",
    display: "flex",
    flexDirection: "column",
    gap: 5,
    minWidth: 0,
  },

  formFieldFull: {
    gridColumn: "1 / -1",
    display: "flex",
    flexDirection: "column",
    gap: 5,
    minWidth: 0,
  },

  formLabel: {
    fontSize: 11.5,
    fontWeight: 900,
    color: "#334155",
  },

  formInput: {
    height: 34,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#111827",
    padding: "0 9px",
    fontSize: 12.5,
    fontWeight: 700,
    outline: "none",
    fontFamily: "inherit",
    boxSizing: "border-box",
  },

  formTextarea: {
    minHeight: 78,
    resize: "vertical",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#111827",
    padding: "8px 9px",
    fontSize: 12.5,
    fontWeight: 700,
    outline: "none",
    fontFamily: "inherit",
    boxSizing: "border-box",
    lineHeight: 1.35,
  },

  formTextareaSmall: {
    minHeight: 54,
    resize: "vertical",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#111827",
    padding: "8px 9px",
    fontSize: 12.5,
    fontWeight: 700,
    outline: "none",
    fontFamily: "inherit",
    boxSizing: "border-box",
    lineHeight: 1.35,
  },

  formDivider: {
    marginTop: 2,
    padding: "8px 10px",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    color: "#334155",
    fontSize: 12,
    fontWeight: 900,
    display: "flex",
    alignItems: "center",
    gap: 7,
  },

  uploadBox: {
    minHeight: 58,
    border: "1px dashed #94a3b8",
    background: "#f8fafc",
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 11px",
    cursor: "pointer",
    boxSizing: "border-box",
  },

  uploadBoxIcon: {
    width: 34,
    height: 34,
    border: "1px solid #dbe2ea",
    background: "#ffffff",
    color: "#334155",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  uploadBoxText: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    minWidth: 0,
    color: "#334155",
    fontSize: 12,
    fontWeight: 750,
  },

  hiddenFileInput: {
    display: "none",
  },

  overlayFooter: {
    padding: "12px 18px 16px",
    borderTop: "1px solid #e5e7eb",
    background: "#f8fafc",
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
  },

  cancelButton: {
    height: 34,
    padding: "0 13px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    fontSize: 12,
    fontWeight: 850,
    cursor: "pointer",
    fontFamily: "inherit",
  },

  saveButton: {
    height: 34,
    padding: "0 14px",
    border: "1px solid",
    color: "#ffffff",
    fontSize: 12,
    fontWeight: 900,
    cursor: "pointer",
    fontFamily: "inherit",
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
  },
};

export default CertidoesDocumentosLayout;
